import { Auth0Provider as Auth0ReactProvider } from "@auth0/auth0-react";
import type { AppState } from "@auth0/auth0-react";
import type { ReactNode } from "react";

type Props = {
  children: ReactNode;
};

// Module-scoped slot used to ferry the original location across the Auth0
// redirect round-trip. ProtectedRoute writes it into `appState.returnTo`
// before calling loginWithRedirect; onRedirectCallback stores it here when
// Auth0 hands control back; LoginCallback drains it to navigate the SPA.
let pendingReturnTo: string | null = null;

export function consumePendingReturnTo(): string | null {
  const value = pendingReturnTo;
  pendingReturnTo = null;
  return value;
}

// Test-only: seed the pending-returnTo slot to simulate what
// onRedirectCallback would have written after a real Auth0 round-trip.
// Production code should never call this — onRedirectCallback owns writes.
export function __primePendingReturnToForTests(value: string | null): void {
  pendingReturnTo = value;
}

export function Auth0Provider({ children }: Props) {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  if (!domain || !clientId || !audience) {
    throw new Error(
      "Missing Auth0 environment variables. Populate VITE_AUTH0_DOMAIN, " +
        "VITE_AUTH0_CLIENT_ID, and VITE_AUTH0_AUDIENCE in apps/web/.env.local. " +
        "See apps/web/.env.example for the placeholder shape.",
    );
  }

  return (
    <Auth0ReactProvider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience,
      }}
      // Persist the session + rotating refresh token across reloads. With
      // "memory" the token was lost on every refresh, forcing a visible
      // loginWithRedirect -> /callback round-trip ("Redirecting to sign in" /
      // "Completing sign in") even for an already-signed-in user. localStorage
      // lets the SDK silently restore the session on load, so a refresh stays
      // on the current page. Refresh-token rotation (Auth0 SPA default) bounds
      // the XSS exposure that comes with localStorage-cached tokens.
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={false}
      onRedirectCallback={(appState?: AppState) => {
        const returnTo = appState?.returnTo;
        pendingReturnTo = typeof returnTo === "string" ? returnTo : null;
      }}
    >
      {children}
    </Auth0ReactProvider>
  );
}
